import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import zoomSdk from "@zoom/appssdk";
import Column from "@amzn/meridian/esm/components/column";
import { AIMS_NOT_AVAILABLE_MESSAGE, AIMS_STATUS, TABS, ROLES } from "../../constants";
import AimsMasthead from "../common/AimsMasthead";
import Summaries from "../inMeeting/summary/Summaries";
import SummarizeBox from "../organizer/SummarizeBox";
import Help from ".././inMeeting/Help";
import Loading from "../common/Loading";
import AttendeeHomeView from "../attendee/AttendeeHomeView";
import AimsNotAvailable from "../organizer/AimsNotAvailable";
import AimsException from "../common/AimsException";
import LogManager from "../utils/LogManager";
import { fetchAimsAvailabilityApi } from "../utils/AimsAvailabilityUtil";
import { AimsAvailabilityApiResponse } from "../../localtest/LocalhostTest";
import { fetchRtmsSessionStatus, SESSION_STATUS } from "../utils/RtmsSessionStatusUtils";
import { fetchPaginatedSummaries } from "../utils/SummaryUtil";

/**
 * This is the main entry point for the AiMS Zoom App.
 *
 * - It initializes the Zoom SDK and, based on the running context (where the app is running)
 *   and user role (host or attendee), renders the appropriate view.
 * - If the user is the meeting organizer and is in a meeting, it calls the `/is-aims-available` API
 *   to determine the meeting type.
 *   The meeting type impacts:
 *     1) Whether the host can see the "Summarize" button (whether AiMS can be enabled)
 *     2) Who will receive the generated meeting summary
 */
const Home = ({ lastKnownContext, setLastKnownContext }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const defaultTab = location.state?.selectedTab || "main";
  const [isLoading, setIsLoading] = useState(true);
  const [role, setRole] = useState(null);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [isPrefetchComplete, setIsPrefetchComplete] = useState(false);

  //TODO: change AIMS status to null as default status once the bug fixed: https://sim.amazon.com/issues/MEETEX-169856
  const [aimsStatus, setAimsStatus] = useState(AIMS_STATUS.AVAILABLE);

  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const [runningContext, setRunningContext] = useState("inMeeting");
  const [userContext, setUserContext] = useState({});
  const [meetingUUID, setMeetingUUID] = useState(null);
  const [meetingId, setMeetingId] = useState(null);
  const [aimsNotAvailableMessage, setAimsNotAvailableMessage] = useState(AIMS_NOT_AVAILABLE_MESSAGE.DEFAULT);
  const [attendeeSessionStatus, setAttendeeSessionStatus] = useState(null);
  const [alias, setAlias] = useState(null);
  const [appContext, setAppContext] = useState(null);
  const [isRtmsStartedByOther, setIsRtmsStartedByOther] = useState(false);
  const [rtmsRequesterName, setRtmsRequesterName] = useState(null);

  /**
   * Process AIMS availability response and update component state accordingly.
   * @param {object} aimsAvailability - Parsed JSON object from AIMS availability API.
   */
  const handleAimsAvailabilityResult = (aimsAvailability) => {
    if (!aimsAvailability) {
      LogManager.warn("The aimsAvailability value is null, show exception view.");
      setAimsStatus(AIMS_STATUS.EXCEPTION);
      return;
    }

    const {
      result: AimsAvailableResult,
      message: AimsAvailableMessage,
      hasPandC,
      warningFromZoom,
      warningFromOutlook,
      hasExternalUser,
      role,
      alias,
    } = aimsAvailability;

    // Set user role information
    setRole(role);
    setAlias(alias);
    LogManager.info(`User alias: ${alias}, role: ${role}`);

    if (AimsAvailableResult === "AimsAvailable") {
      if (hasPandC) {
        LogManager.info("This is privileged_and_confidential meeting.");
        setAimsStatus(AIMS_STATUS.SUMMARY_TO_ORGANIZER);
      } else {
        LogManager.info("This is low risk meeting.");
        setAimsStatus(AIMS_STATUS.AVAILABLE);
      }
    } else if (AimsAvailableResult === "AimsNotAvailable") {
      LogManager.info("This is AimsNotAvailable meeting.");
      setAimsStatus(AIMS_STATUS.NOT_AVAILABLE);
      if (AimsAvailableMessage === "work_council") {
        setAimsNotAvailableMessage(AIMS_NOT_AVAILABLE_MESSAGE.REGIONS);
      } else if (AimsAvailableMessage === "performance_review") {
        setAimsNotAvailableMessage(AIMS_NOT_AVAILABLE_MESSAGE.PERFORMANCE)
      }
    } else {
      LogManager.warn("Unknown AimsAvailableResult value:", AimsAvailableResult);
      setAimsStatus(AIMS_STATUS.EXCEPTION);
    }
  };

  /**
   * Initialize Zoom SDK, fetch meeting and user context and check AIMS availability.
   */
  const initializeZoomSdk = async () => {
    if (window.location.href === "http://localhost:3000/") {
      console.log("[Localhost] Skipping zoomSdk.config() in localhost");
      return;
    }
    try {
      const configResponse = await zoomSdk.config({
        popoutSize: { width: 320, height: 760 },
        capabilities: [
          "getAppContext",
          "getMeetingContext",
          "getRunningContext",
          "getUserContext",
          "openUrl",
          "getMeetingUUID",
          "authorize",
          "onAuthorized",
          "sendMessageToChat",
          "onRTMSStatusChange",
          "getRTMSStatus",
        ],
      });
      LogManager.info("Zoom SDK Configured Successfully:", configResponse);

      const runningContext = await zoomSdk.getRunningContext();
      LogManager.info("The runningContext is:", runningContext);

      console.log("Document Visibility State:", document.visibilityState);
      setRunningContext(runningContext.context);
      setLastKnownContext(runningContext.context);

      if (runningContext.context === "inMainClient") {
        //TODO: Logging - find a identifier in inMainClient for the user
        LogManager.info("The app is running in main client now, show MainClientHome view.");
        return;
      }

      //The runningContext is inMeeting
      const meetingUUID = (await zoomSdk.getMeetingUUID()).meetingUUID;
      console.log("The meetingUUID is:" + meetingUUID);
      setMeetingUUID(meetingUUID);

      const userContext = await zoomSdk.getUserContext();
      LogManager.info("The userContext is:", userContext);
      setUserContext(userContext);

      // First, make a unified session status call for all user types (attendee, organizer/althos)
      LogManager.info("Fetching the RTMS status from backend for all user types...");
      const sessionStatus = await fetchRtmsSessionStatus(meetingUUID);
      LogManager.info("RTMS status is", sessionStatus);

      // Process session status information for all user types
      if (sessionStatus.status === SESSION_STATUS.ACTIVE) {
        // Set summarizing state for all user types
        setIsSummarizing(true);
        
        // Determine who started the session for all user types
        if (!sessionStatus.rtmsRequesterAlias) {
          // No requester info available - session started by someone else
          setIsRtmsStartedByOther(true);
          setRtmsRequesterName("Someone");
        } else if (sessionStatus.rtmsRequesterAlias !== userContext.screenName) {
          // Started by someone else
          setIsRtmsStartedByOther(true);
          setRtmsRequesterName(sessionStatus.rtmsRequesterName || "Someone");
        } else {
          // Started by current user
          setIsRtmsStartedByOther(false);
          setRtmsRequesterName(null);
        }
      } else {
        LogManager.info("No RTMS session found");
        setIsSummarizing(false);
        setIsRtmsStartedByOther(false);
        setRtmsRequesterName(null);
      }

      //Call aimsAvailability api
      if (["host", "coHost"].includes(userContext.role)) {
        LogManager.info("The app is running inMeeting and user is host or coHost.");

        //Only host and coHost can call this api
        const meetingContext = await zoomSdk.getMeetingContext();
        LogManager.info("The meetingContext is: ", meetingContext);
        setMeetingId(meetingContext.meetingID);

        const aimsAvailability = await fetchAimsAvailabilityApi(
          meetingUUID,
          meetingContext.meetingID,
          meetingContext.meetingTopic,
          userContext.screenName
        );
        handleAimsAvailabilityResult(aimsAvailability);
        
        // Get app context for secure backend validation (only needed for organizers/alternative hosts)
        const appContextResponse = await zoomSdk.getAppContext();
        LogManager.info("app context response is: " + JSON.stringify(appContextResponse));
        
        if (!appContextResponse || !appContextResponse.context) {
          LogManager.error("getAppContext() returned invalid response - cannot proceed with RTMS operations");
          setAimsStatus(AIMS_STATUS.EXCEPTION);
        } else {
          setAppContext(appContextResponse.context);
        }
        
        LogManager.info("Finish the initialization for host or coHost.");

      } else {
        LogManager.info("The app is running inMeeting and user is attendee.");
        
        // Set role to attendee directly without calling availability API
        setRole(ROLES.ATTENDEE);
        
        // Store session status for attendee (using the unified session call made above)
        setAttendeeSessionStatus(sessionStatus.status);
      }
    } catch (error) {
      LogManager.error("Error fetching context or call AimsAvailability/RTMS Status API:", error);
      setAimsStatus(AIMS_STATUS.EXCEPTION);
    } finally {
      //Quick fix: fix prod bug
      // const persistedAimsStatus = localStorage.getItem('RtmsSessionStatus');
      // if (persistedAimsStatus === "ACTIVE") {
      //   setIsSummarizing(true);
      // }
      setIsLoading(false);
    }
  };

  // Main effect: configure SDK and fetch AIMS status
  useEffect(() => {
    // Localhost testing override
    if (window.location.href === "http://localhost:3000/") {
      console.log("[Localhost] Running in localhost mode.");
      handleAimsAvailabilityResult(AimsAvailabilityApiResponse);
      setRole(ROLES.ORGANIZER);
      //Running context value: inMeeting, inMainClient
      setRunningContext("inMeeting");
      setMeetingUUID("testUUID");
      setAimsStatus(AIMS_STATUS.SUMMARY_TO_ORGANIZER);
      //Quick fix: remove prod bug
      // localStorage.setItem('RtmsSessionStatus', SESSION_STATUS.ACTIVE);
      setIsLoading(false);
      setIsSummarizing(false);
      setIsPrefetchComplete(true); // Mark prefetch as complete for localhost
    } else {
      initializeZoomSdk();
      // Wait for prefetch to complete
      fetchPaginatedSummaries(true)
        .then(result => {
          setIsPrefetchComplete(true); // Mark prefetch as complete
        })
        .catch(error => {
          LogManager.error("Error during summaries prefetch:", error);
          setIsPrefetchComplete(true); // Still mark as complete even on error to avoid blocking UI
        });
    }
  }, []);

  // redirect to /client when inMainClient and prefetch is complete
  useEffect(() => {
    if (runningContext === "inMainClient" && isPrefetchComplete) {
      LogManager.info("Detected inMainClient context and prefetch is complete, redirecting to /client.");
      navigate("/client");
    }
  }, [runningContext, navigate, isPrefetchComplete]);

  // Show loading when in client context and waiting for prefetch
  if (runningContext === "inMainClient" && !isPrefetchComplete) {
    return <Loading />;
  }

  return (
    <Column spacing="none">
      <AimsMasthead selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
      {selectedTab === TABS.SUMMARIES && isPrefetchComplete ? (
        <Summaries />
      ) : selectedTab === TABS.HELP ? (
        <Help />
      ) : selectedTab === TABS.MAIN ? (
        isLoading ? (
          <Loading />
        ) : aimsStatus === AIMS_STATUS.EXCEPTION ? (
          <AimsException />
        ) : aimsStatus === AIMS_STATUS.NOT_AVAILABLE ? (
          <AimsNotAvailable aimsNotAvailableMessage={aimsNotAvailableMessage} />
        ) : (role === ROLES.ORGANIZER || role === ROLES.ALTERNATIVE_HOST) ? (
          <SummarizeBox
            isSummarizing={isSummarizing}
            setIsSummarizing={setIsSummarizing}
            meetingId={meetingId}
            meetingUuid={meetingUUID}
            aimsStatus={aimsStatus}
            role={role}
            alias={alias}
            appContext={appContext}
            initialIsRtmsStartedByOther={isRtmsStartedByOther}
            initialRtmsRequesterName={rtmsRequesterName}
          />
        ) : (
          <AttendeeHomeView 
            meetingUUID={meetingUUID} 
            aimsStatus={AIMS_STATUS.AVAILABLE}
            initialSessionStatus={attendeeSessionStatus}
            initialRtmsRequesterName={rtmsRequesterName}
          />
        )
      ) : null}
    </Column>
  );
};

export default Home;
